import { AESCipherSIVNonce96 } from '../../base/cipher/aes';
import { AbstractSelfAccountKeyring } from '../../keyring/account_base';
import { PeerAccountKeyring } from '../../keyring/account_peer';
import { AbstractRoomKeyring } from '../../keyring/room_base';
import { makeInfoByteArray } from '../../utility';
import { AbstractCryptoConfig, CryptoConfigVersioning } from '../base';
import { AccountPairDeriveKeyV2, KemSharedSecretSelectorV1, TS_WINDOW_V1 } from '../common';
import { NanoConfigDumpArgs, NanoConfigLoadArgs } from './args';
import { CryptoUpgrade } from '../feature';

/**
 *     Nano message (request and initial response) encryption: AES-SIV
 *         KEY: 32B from curve25519 scalar_mult + 32B from MLKEM1024 (ss-id encoded in version by acc-id)
 *             -> 64B HKDF by context + room-key + current time-slice-window
 *         NONCE: 12B random
 *
 *     Using a room-key disables anonymous identities without a share-url to be able to request anything. The
 *     anonymously shared room has no strong confidentiality, yet this provides a loose confidentiality option.
 *     As long as the share url is securely distributed and all recipients keep it a secret, the room will be
 *     similarly private as with membership access control.
 *
 *     NOTE: The room secret is used from a RoomKeyring that is loaded from an encryption that uses the room-id
 *     in the salt (account_key.room and room_key). Since all encryption ciphertexts are authenticated, the keys
 *     in the room-keyring are guaranteed to be authentic and also for the specific room. Thus using a room-key
 *     indirectly binds derived key here to the room-id as well without explicitly using it.
 */
class _NanoConfigV2V3 extends AbstractCryptoConfig<NanoConfigLoadArgs, NanoConfigDumpArgs> {
  public other_version: Uint8Array;
  public static TS_WINDOW = TS_WINDOW_V1;

  constructor(version: number, other_version: number, cu: CryptoUpgrade) {
    const other_config_version = CryptoConfigVersioning.config_version_from_int(other_version);
    const config_version = CryptoConfigVersioning.config_version_from_int(version);
    if (other_config_version === null || config_version === null) {
      throw new Error('Invalid version number for crypto config');
    }
    super(version, cu, [config_version, other_config_version]);
    this.other_version = other_config_version;
  }

  public load(args: NanoConfigLoadArgs): Promise<Uint8Array> {
    return new Promise((resolve, reject) => {
      let error;

      let cipher = args.cipher.slice(this.version.byteLength);
      let timestampArr = _NanoConfigV2V3.TS_WINDOW.windows(args.timestamp);

      let _load = (timestamp_index) => {
        if (timestamp_index >= timestampArr.length) {
          reject(error);
          return;
        }

        let timestamp_window = timestampArr[timestamp_index];

        this.derive_key(
          KemSharedSecretSelectorV1.select_ss_for_load(
            args.self_kr,
            args.peer_kr,
            this.version,
            this.other_version
          ),
          this.version,
          args.self_kr,
          args.peer_kr,
          args.room_kr.get_key(AbstractRoomKeyring.PINNED),
          timestamp_window
        )
          .then((derive_key) => {
            AESCipherSIVNonce96.decrypt(cipher, derive_key)
              .then((res) => {
                resolve(res);
              })
              .catch((err) => {
                error = err;
                _load(timestamp_index + 1);
              });
          })
          .catch(reject);
      };

      _load(0);
    });
  }

  public dump(args: NanoConfigDumpArgs): Promise<Uint8Array> {
    const ssv = KemSharedSecretSelectorV1.select_ss_version_for_dump(
      args.self_kr,
      args.peer_kr,
      this.version,
      this.other_version
    );
    return this.derive_key(
      ssv.shared_secret,
      ssv.selected_version,
      args.self_kr,
      args.peer_kr,
      args.room_kr.get_key(AbstractRoomKeyring.PINNED),
      _NanoConfigV2V3.TS_WINDOW.window(args.timestamp)
    ).then((derive_key) => {
      return AESCipherSIVNonce96.encrypt(
        args.plain,
        derive_key,
        undefined,
        undefined,
        ssv.selected_version
      );
    });
  }

  public derive_key(
    shared_secret: Uint8Array,
    version: Uint8Array,
    self_kr: AbstractSelfAccountKeyring,
    peer_kr: PeerAccountKeyring,
    room_secret: Uint8Array,
    ts_window: Uint8Array
  ) {
    return AccountPairDeriveKeyV2.derive_key(
      shared_secret,
      self_kr,
      peer_kr,
      makeInfoByteArray(['nano-', version, '-', room_secret, '-', ts_window])
    );
  }
}

export class NanoConfigV2 extends _NanoConfigV2V3 {
  constructor(cu: CryptoUpgrade) {
    super(2, 3, cu);
  }
}

export class NanoConfigV3 extends _NanoConfigV2V3 {
  constructor(cu: CryptoUpgrade) {
    super(3, 2, cu);
  }
}
